import { usePaymentAuthorizationReturn } from '@/features/payment/authorization'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useCallback, useMemo, useState } from 'react'
import {
  AppSidebar,
  type SidebarHistoryItem,
  type SidebarNavItem,
} from '@/components/business/app-sidebar'
import { AppHeader } from '@/components/business/app-header'
import { ActionConfirmDialog, ActionTextInputDialog } from '@/components/business/action-dialog'
import { usePageHeaderState } from '@/components/business/page-header-context'
import { GuidedTour, type GuidedTourStep } from '@/components/business/guided-tour'
import { LoginDialog } from '@/components/business/LoginDialog'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import {
  AI_SESSION_HISTORY_PAGE,
  compareAiSessionsByUpdateTimeDesc,
  countActiveGuestConversations,
  useDeleteAiSession,
  useMyAiSessions,
  usePinAiSession,
  useRenameAiSession,
} from '@/features/ai-doc/api'
import { useConsultUnreadCount } from '@/features/consult/api'
import { ConsultMessageNotifier } from '@/features/consult/components/consult-message-notifier'
import { NotificationGuidedTour } from '@/features/notification/components/notification-guided-tour'
import { getLawyerEntryPath } from '@/features/lawyer-cert/navigation'
import { useResponsiveSidebar } from '@/hooks/use-responsive-sidebar'
import { useMenuLogoUrl, useSiteBrief, useSiteTitle } from '@/features/site/api'
import {
  AI_GUEST_CONVERSATION_LIMIT_MESSAGE,
  useAiGuestAccessStore,
} from '@/features/ai-doc/guest-access'
import { createAiHistorySelectionState } from '@/features/ai-doc/auto-resume'
import type { LegalAiConversationMetaVo } from '@/types/api'
import { toast } from 'sonner'

const NAV_ITEMS_AUTHED: SidebarNavItem[] = [
  // 值班律师页面暂不展示，后续需要时恢复此入口即可（路由仍保留）
  // { to: '/u/lawyers', label: '值班律师', icon: 'gavel' },
  { to: '/u/packages', label: '权益套餐', icon: 'workspace_premium' },
  { to: '/u/consult', label: '消息', icon: 'chat' },
]

const NAV_ITEMS_GUEST: SidebarNavItem[] = []

const AI_HEADER_SUBTITLE = '内容由 AI 生成，请仔细甄别'

const AI_DOC_GUIDED_TOUR_STEPS: GuidedTourStep[] = [
  {
    id: 'new-chat',
    target: '[data-tour="ai-doc-new-chat"]',
    title: '开启新的法律任务',
    description: '从这里随时新建对话，用于生成文书、梳理案情或开始新的法律问题。',
    placement: 'right',
  },
  {
    id: 'composer',
    target: '[data-tour="ai-doc-composer"]',
    title: '描述你的问题',
    description: '直接输入案情、诉求、证据线索。AI 会根据你的描述继续追问关键信息。',
    placement: 'top',
  },
  {
    id: 'upload',
    target: '[data-tour="ai-doc-upload"]',
    title: '上传材料辅助分析',
    description: '合同、截图、图片或其他材料都可以上传，帮助 AI 更准确地生成文书。',
    placement: 'top',
  },
  {
    id: 'document',
    target: '[data-tour="ai-doc-document"]',
    title: '查看文书并下载',
    description: '文书生成完成后，这里会展示预览、下载和付费解锁入口。',
    placement: 'left',
  },
]

const TITLE_MAP: Record<string, string> = {
  '/u': '我的',
  '/u/ai-doc': '新对话',
  '/u/lawyers': '律师列表',
  '/u/lawyers/followed': '我的关注',
  '/u/packages': '权益套餐',
  '/u/consult': '消息',
  '/u/orders': '我的订单',
  '/u/settings': '设置',
  '/u/profile/edit': '编辑资料',
}

function getSessionTitle(session: LegalAiConversationMetaVo): string {
  return session.title || session.lastQuestion || '新对话'
}

function buildAiHistoryItem(
  session: LegalAiConversationMetaVo,
  activeConversationId: string | null,
): SidebarHistoryItem {
  const conversationId = session.conversationId ?? ''
  return {
    id: conversationId,
    title: getSessionTitle(session),
    to: `/u/ai-doc?conversationId=${encodeURIComponent(conversationId)}`,
    state: createAiHistorySelectionState(conversationId),
    icon: session.currentStep === 'completed' ? 'description' : 'chat',
    active: Boolean(conversationId && conversationId === activeConversationId),
    isPinned: Boolean(session.pinned),
  }
}

/**
 * 用户端布局
 *
 * 设计参考: ui/.../user_profile_dashboard 的整体结构 (320px sidebar + h-16 header + flex 主区).
 */
export function UserLayout() {
  usePaymentAuthorizationReturn()
  const location = useLocation()
  const navigate = useNavigate()
  const { sidebarCollapsed, setSidebarCollapsed, closeSidebarOnMobile } = useResponsiveSidebar()
  const siteTitle = useSiteTitle()
  const menuLogoUrl = useMenuLogoUrl()
  const siteBrief = useSiteBrief()
  const pageHeader = usePageHeaderState()
  const userInfo = useAuthStore((s) => s.userInfo)
  const roles = useAuthStore((s) => s.roles)
  const logout = useAuthStore((s) => s.logout)
  const openLoginModal = useUiStore((s) => s.openLoginModal)
  const loginModalOpen = useUiStore((s) => s.loginModalOpen)
  const loginActionDisabled = useUiStore((s) => s.loginActionDisabled)
  const isAuthenticated = useAuthStore(
    (s) => s.accessToken !== null && (!s.expiresAt || Date.now() < s.expiresAt),
  )
  const isLoggingOut = useAuthStore((s) => s.isLoggingOut)
  const guestToken = useAiGuestAccessStore((s) => s.guestToken)
  const guestExpiresAt = useAiGuestAccessStore((s) => s.expiresAt)
  const guestMaxConversations = useAiGuestAccessStore((s) => s.maxConversations)
  const [guestAccessCheckTime] = useState(() => Date.now())
  const hasGuestAccess = Boolean(
    guestToken &&
    (!guestExpiresAt || Date.parse(guestExpiresAt.replace(' ', 'T')) > guestAccessCheckTime),
  )
  const hasConversationAccess = !isLoggingOut && (isAuthenticated || hasGuestAccess)
  const activeConversationId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('conversationId') ?? params.get('sessionUuid')
  }, [location.search])
  const sessionsQuery = useMyAiSessions(AI_SESSION_HISTORY_PAGE, {
    enabled: hasConversationAccess,
  })
  const pinAiSession = usePinAiSession()
  const renameAiSession = useRenameAiSession()
  const deleteAiSession = useDeleteAiSession()
  const [renameTarget, setRenameTarget] = useState<SidebarHistoryItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<SidebarHistoryItem | null>(null)
  const consultUnreadQuery = useConsultUnreadCount('user', {
    enabled: isAuthenticated,
    refetchInterval: 60_000,
  })
  const consultUnreadCount = consultUnreadQuery.data ?? 0
  const navItems = useMemo<SidebarNavItem[]>(
    () =>
      NAV_ITEMS_AUTHED.map((item) =>
        item.to === '/u/consult' ? { ...item, badgeCount: consultUnreadCount } : item,
      ),
    [consultUnreadCount],
  )
  const historyItems = useMemo(
    () =>
      (sessionsQuery.data?.rows ?? [])
        .slice()
        .sort(compareAiSessionsByUpdateTimeDesc)
        .filter((session) => Boolean(session.conversationId))
        .map((session) => buildAiHistoryItem(session, activeConversationId)),
    [activeConversationId, sessionsQuery.data],
  )
  const accountName = userInfo?.nickname || userInfo?.username || '访客'
  const isAiDocRoute = location.pathname === '/u/ai-doc'
  const isConsultRoute = location.pathname === '/u/consult'
  const isNewAiDocActive = isAiDocRoute && !activeConversationId
  const activeHistoryTitle = useMemo(
    () => historyItems.find((item) => item.active)?.title,
    [historyItems],
  )

  function handleNewAiDocSession() {
    if (
      !isAuthenticated &&
      guestMaxConversations !== null &&
      countActiveGuestConversations(sessionsQuery.data?.rows ?? []) >= guestMaxConversations
    ) {
      toast.info(AI_GUEST_CONVERSATION_LIMIT_MESSAGE)
      return
    }
    navigate(`/u/ai-doc?new=${Date.now()}`)
  }

  const handlePinAiSession = useCallback(
    async (item: SidebarHistoryItem) => {
      const pinned = !item.isPinned
      await pinAiSession.mutateAsync({ conversationId: item.id, pinned })
      toast.success(pinned ? '已置顶会话' : '已取消置顶')
    },
    [pinAiSession],
  )

  const handleRenameAiSession = useCallback((item: SidebarHistoryItem) => {
    setRenameTarget(item)
  }, [])

  const handleDeleteAiSession = useCallback((item: SidebarHistoryItem) => {
    setDeleteTarget(item)
  }, [])

  const handleConfirmRenameAiSession = useCallback(
    async (title: string) => {
      if (!renameTarget) return

      await renameAiSession.mutateAsync({ conversationId: renameTarget.id, title })
      setRenameTarget(null)
      toast.success('已重命名会话')
    },
    [renameAiSession, renameTarget],
  )

  const handleConfirmDeleteAiSession = useCallback(async () => {
    if (!deleteTarget) return

    await deleteAiSession.mutateAsync(deleteTarget.id)
    const deletedActiveSession = deleteTarget.active
    setDeleteTarget(null)
    toast.success('已删除会话')
    if (deletedActiveSession) {
      navigate('/u/ai-doc', { replace: true })
      closeSidebarOnMobile()
    }
  }, [closeSidebarOnMobile, deleteAiSession, deleteTarget, navigate])

  function handleLogout() {
    logout()
    toast.success('已退出登录')
    navigate('/', { replace: true })
  }

  // 精确命中或最长前缀命中
  const routeTitle =
    TITLE_MAP[location.pathname] ??
    Object.entries(TITLE_MAP)
      .filter(([k]) => location.pathname.startsWith(k + '/'))
      .sort((a, b) => b[0].length - a[0].length)[0]?.[1] ??
    '工作台'
  const aiDocTitle = isAiDocRoute ? activeHistoryTitle || '新对话' : routeTitle
  // 页面通过 usePageHeader 上报的标题/副标题/返回优先; AI 文书页保留原有兜底.
  const headerTitle = pageHeader.title ?? (isAiDocRoute ? aiDocTitle : undefined)
  const subtitle = pageHeader.subtitle ?? (isAiDocRoute ? AI_HEADER_SUBTITLE : undefined)
  const headerOnBack = pageHeader.back
  const showAiDocGuidedTour = isAuthenticated && isAiDocRoute && !loginModalOpen
  const showNotificationGuidedTour = isAuthenticated && !loginModalOpen && isConsultRoute

  return (
    <div className="text-on-surface flex h-dvh overflow-hidden">
      <ConsultMessageNotifier />
      {!sidebarCollapsed && (
        <button
          type="button"
          aria-label="关闭侧边栏"
          className="bg-inverse-surface/30 fixed inset-0 z-30 backdrop-blur-[18px] backdrop-saturate-150 md:hidden"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}
      <AppSidebar
        brandTitle={siteTitle}
        brandIcon={menuLogoUrl}
        brandSubtitle={siteBrief || '智能法律顾问'}
        collapsed={sidebarCollapsed}
        onNavigate={closeSidebarOnMobile}
        items={isAuthenticated ? navItems : NAV_ITEMS_GUEST}
        primaryAction={{
          label: '新对话',
          icon: 'edit',
          active: isNewAiDocActive,
          tourId: 'ai-doc-new-chat',
          onClick: handleNewAiDocSession,
        }}
        historyItems={hasConversationAccess ? historyItems : []}
        historyItemActions={
          isAuthenticated
            ? {
                onPin: handlePinAiSession,
                onRename: handleRenameAiSession,
                onDelete: handleDeleteAiSession,
              }
            : undefined
        }
        historyLoading={hasConversationAccess && sessionsQuery.isLoading}
        account={
          isAuthenticated
            ? {
                name: accountName,
                avatar: userInfo?.avatar,
                switchEntry: {
                  label: '切换到律师端',
                  icon: 'dashboard',
                  onClick: () => navigate(getLawyerEntryPath(roles)),
                },
                onProfile: () => navigate('/u'),
                onSettings: () => navigate('/u/settings'),
                onLogout: handleLogout,
              }
            : undefined
        }
        guestAction={
          isAuthenticated
            ? undefined
            : {
                label: '登录',
                description: '登录后使用全部功能',
                icon: 'person',
                disabled: isAiDocRoute && loginActionDisabled,
                onClick: openLoginModal,
              }
        }
      />
      {renameTarget && (
        <ActionTextInputDialog
          key={renameTarget.id}
          open={Boolean(renameTarget)}
          title="编辑对话名称"
          initialValue={renameTarget.title}
          inputLabel="对话名称"
          confirmText="确定"
          pending={renameAiSession.isPending}
          onOpenChange={(open) => {
            if (!open) setRenameTarget(null)
          }}
          onConfirm={handleConfirmRenameAiSession}
        />
      )}
      {deleteTarget && (
        <ActionConfirmDialog
          key={deleteTarget.id}
          open={Boolean(deleteTarget)}
          title="确定删除多对话？"
          description="删除后，聊天记录将不可恢复。"
          confirmText="删除"
          tone="danger"
          iconName="warning-circle"
          pending={deleteAiSession.isPending}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null)
          }}
          onConfirm={handleConfirmDeleteAiSession}
        />
      )}
      <main className="app-page-surface mx-auto flex min-h-0 min-w-0 flex-1 flex-col">
        <AppHeader
          title={headerTitle}
          titleHref={pageHeader.titleHref}
          subtitle={subtitle}
          subtitleDetails={pageHeader.subtitleDetails}
          onBack={headerOnBack}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed((collapsed) => !collapsed)}
        />
        <div className="app-page-surface min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>
      <GuidedTour
        open={showAiDocGuidedTour}
        steps={AI_DOC_GUIDED_TOUR_STEPS}
        storageKey="legal-web:tour:user-ai-doc:v1"
      />
      <NotificationGuidedTour open={showNotificationGuidedTour} />
      <LoginDialog />
    </div>
  )
}
